import { formatMoneyMinor, isBrowser } from "@repo/vihangam-yog-shared";
import { useEffect, useState, type FormEvent } from "react";

import { track } from "../analytics";
import { DONATE } from "../content/donate";
import { Page } from "../layout/Page";
import { requestErrorMessage } from "../requestError";
import { trpc } from "../trpc";
import { Button } from "../ui/Button";
import { Field } from "../ui/Field";
import { Section } from "../ui/Section";

const CURRENCY = "sgd";
/** Suggested amounts in SGD cents: S$20 / S$50 / S$100. The backend
 *  clamps the final figure, so these are only a starting point. */
const PRESETS_CENTS = [2000, 5000, 10000];

function readThanks(): boolean {
  if (!isBrowser()) {
    return false;
  }
  try {
    return new URLSearchParams(window.location.search).get("thanks") === "1";
  } catch {
    return false;
  }
}

function CardOption() {
  const [cents, setCents] = useState<number>(PRESETS_CENTS[0] ?? 2000);
  const [customAmount, setCustomAmount] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const donate = trpc.createDonationCheckout.useMutation();
  const busy = donate.isPending;

  function pickPreset(value: number) {
    setCents(value);
    setCustomAmount("");
  }

  function onCustomAmountChange(value: string) {
    setCustomAmount(value);
    const dollars = Number(value);
    setCents(
      Number.isFinite(dollars) && dollars > 0 ? Math.round(dollars * 100) : 0,
    );
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (cents <= 0) {
      setError("Please choose an amount to donate.");
      return;
    }
    donate.mutate(
      {
        amountCents: cents,
        name: name.trim() || undefined,
        email: email.trim() || undefined,
      },
      {
        onSuccess: (data) => {
          track("donate_started");
          window.location.href = data.url;
        },
        onError: (err) =>
          setError(
            requestErrorMessage(
              err,
              "Sorry, we couldn't start the payment just now. Please try again in a moment.",
            ),
          ),
      },
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate className="mt-4 grid gap-4">
      <div>
        <p id="donate-amount-label" className="text-sm font-semibold text-ink">
          Amount
        </p>
        <div
          className="mt-2.5 flex flex-wrap gap-2"
          role="group"
          aria-labelledby="donate-amount-label"
        >
          {PRESETS_CENTS.map((p) => {
            const active = cents === p && !customAmount;
            return (
              <button
                key={p}
                type="button"
                onClick={() => pickPreset(p)}
                disabled={busy}
                aria-pressed={active}
                className={`min-h-11 rounded-full border px-4 text-sm font-medium transition-colors ${
                  active
                    ? "border-magenta bg-magenta text-white"
                    : "border-hairline-strong text-ink hover:border-magenta"
                }`}
              >
                {formatMoneyMinor(p, CURRENCY)}
              </button>
            );
          })}
        </div>
      </div>
      <Field
        label="Other amount"
        htmlFor="donate-other"
        hint="In Singapore dollars."
      >
        <input
          id="donate-other"
          className="field"
          type="number"
          min={0}
          inputMode="decimal"
          value={customAmount}
          disabled={busy}
          onChange={(e) => onCustomAmountChange(e.target.value)}
          placeholder="0"
        />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Your name (optional)" htmlFor="donate-name">
          <input
            id="donate-name"
            className="field"
            autoComplete="name"
            value={name}
            disabled={busy}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field
          label="Email (optional)"
          htmlFor="donate-email"
          hint="For your receipt."
        >
          <input
            id="donate-email"
            className="field"
            type="email"
            autoComplete="email"
            value={email}
            disabled={busy}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
      </div>
      {error ? (
        <p
          role="alert"
          className="rounded-md border border-crimson/40 bg-crimson/5 p-3 text-sm text-crimson"
        >
          {error}
        </p>
      ) : null}
      {donate.error && !error ? (
        <p
          role="alert"
          className="rounded-md border border-crimson/40 bg-crimson/5 p-3 text-sm text-crimson"
        >
          {donate.error.message}
        </p>
      ) : null}
      <div>
        <Button type="submit" disabled={busy}>
          {busy ? "Just a moment" : "Donate by card"}
        </Button>
      </div>
    </form>
  );
}

export function DonatePage() {
  const thanks = readThanks();

  useEffect(() => {
    track("donate_viewed");
  }, []);

  return (
    <Page title="Donate" heading="Donate">
      <Section lede={DONATE.thanks}>
        <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
          <div className="rounded-2xl border border-hairline bg-card p-6">
            <p className="eyebrow">1. Card</p>
            <h3 className="font-display mt-2 text-xl text-ink">
              Donate by card
            </h3>
            {thanks ? (
              <div role="status" className="mt-4">
                <p className="text-ink">
                  Thank you. Your donation has been received and a receipt is on
                  its way to your inbox.
                </p>
                <div className="mt-4">
                  <Button href="/donate" variant="secondary">
                    Make another gift
                  </Button>
                </div>
              </div>
            ) : (
              <CardOption />
            )}
          </div>

          <div className="grid gap-6">
            <div className="rounded-2xl border border-hairline bg-card p-6">
              <p className="eyebrow">2. PayNow</p>
              <h3 className="font-display mt-2 text-xl text-ink">
                Scan to pay
              </h3>
              <img
                src={DONATE.paynow.qr}
                alt="PayNow QR code for Vihangam Yoga Singapore"
                className="mt-4 w-40 rounded-md border border-hairline bg-white p-2"
                loading="lazy"
              />
              <p className="mt-3 text-sm text-ink-soft">
                UEN {DONATE.paynow.uen}
              </p>
            </div>

            <div className="rounded-2xl border border-hairline bg-card p-6">
              <p className="eyebrow">3. Bank Transfer</p>
              <h3 className="font-display mt-2 text-xl text-ink">
                Transfer to our account
              </h3>
              <img
                src={DONATE.bank.logo}
                alt={DONATE.bank.name}
                className="mt-4 h-8 w-auto object-contain"
                loading="lazy"
              />
              <p className="mt-3 text-sm font-medium text-ink">
                {DONATE.bank.account}
              </p>
              <p className="text-sm text-ink-soft">{DONATE.bank.number}</p>
            </div>

            {DONATE.paypalUrl ? (
              <div className="rounded-2xl border border-hairline bg-card p-6">
                <p className="eyebrow">4. PayPal</p>
                <h3 className="font-display mt-2 text-xl text-ink">
                  Donate with PayPal
                </h3>
                <div className="mt-4">
                  <Button href={DONATE.paypalUrl} external variant="secondary">
                    Continue to PayPal
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </Section>
    </Page>
  );
}
